import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      const data = err.response?.data;
      if (data?.needsVerification) {
        toast(data.message);
        navigate('/register', { state: { verifyEmail: data.email } });
        return;
      }
      toast.error(data?.message || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', fontFamily: "'Plus Jakarta Sans', Inter, sans-serif" }}>
      <style>{`
        @keyframes pulse-grid { 0%, 100% { opacity: 0.03; } 50% { opacity: 0.07; } }
        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .login-input { width: 100%; padding: 14px 18px; border: 2px solid #1e293b; border-radius: 12px; font-size: 15px; outline: none; background: #0f172a; color: #f1f5f9; transition: border 0.3s; font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }
        .login-input:focus { border-color: #00d4aa; }
        .login-input::placeholder { color: #475569; }
        @media (max-width: 768px) { .login-left { display: none !important; } .login-right { flex: 1 !important; } }
      `}</style>

      {/* Left — Branding */}
      <div className="login-left" style={{ flex: 1, background: '#020617', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px 80px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(0,212,170,0.1) 1px, transparent 1px)', backgroundSize: '32px 32px', animation: 'pulse-grid 4s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '15%', right: '10%', width: 350, height: 350, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,212,170,0.08) 0%, transparent 70%)', filter: 'blur(50px)', animation: 'float 7s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '10%', left: '5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(56,189,248,0.06) 0%, transparent 70%)', filter: 'blur(40px)', animation: 'float 5s ease-in-out infinite 1s' }} />

        <div style={{ position: 'relative', zIndex: 2 }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 14, marginBottom: 60 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 20, color: '#020617', fontFamily: "'DM Serif Display', serif" }}>ES</div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9' }}>EpiSurveillance</div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '2.5px', color: '#00d4aa', textTransform: 'uppercase' }}>Modern Disease Surveillance</div>
            </div>
          </Link>

          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 50, fontWeight: 400, lineHeight: 1.1, color: '#f1f5f9', letterSpacing: '-1px', marginBottom: 24 }}>
            Surveillance That<br />
            <span style={{ background: 'linear-gradient(135deg, #00d4aa 0%, #38bdf8 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Saves Lives.</span>
          </h1>
          <p style={{ fontSize: 17, color: '#64748b', lineHeight: 1.8, maxWidth: 440 }}>
            The most comprehensive disease surveillance platform for modern public health. Real-time tracking, AI-powered insights, and multi-jurisdiction workspaces.
          </p>

          <div style={{ display: 'flex', gap: 36, marginTop: 48 }}>
            {[['15+', 'Modules'], ['Real-Time', 'Alerts'], ['AI', 'Powered']].map(([n, l]) => (
              <div key={l}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#00d4aa', fontFamily: "'DM Serif Display', serif" }}>{n}</div>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="login-right" style={{ flex: 1, background: '#0a0f1e', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <div className="auth-card" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 24, padding: '48px 40px', border: '1px solid #1e293b' }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Welcome Back</h2>
            <p style={{ color: '#64748b', marginBottom: 36, fontSize: 15 }}>Sign in to your surveillance platform</p>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 8, letterSpacing: '0.5px' }}>Email Address</label>
                <input type="email" placeholder="you@organization.gov" className="login-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div style={{ marginBottom: 28 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 8, letterSpacing: '0.5px' }}>Password</label>
                <input type="password" placeholder="Enter your password" className="login-input" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: 16, border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#334155' : 'linear-gradient(135deg, #00d4aa, #00b894)', color: loading ? '#94a3b8' : '#020617',
                fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.3s',
              }}>
                {loading ? 'Signing in...' : 'Sign In →'}
              </button>
            </form>

            <div style={{ marginTop: 28, textAlign: 'center', fontSize: 14, color: '#64748b' }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: '#00d4aa', fontWeight: 700, textDecoration: 'none' }}>Sign Up & Get Access Now</Link>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 32, fontSize: 13 }}>
            <Link to="/" style={{ color: '#475569', textDecoration: 'none' }}>← Back to EpiSurveillance Home</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
